import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const pwaInstallDismissedKey = 'pwa-install-dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(pwaInstallDismissedKey) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(pwaInstallDismissedKey, '1');
  } catch {
    // Storage can be blocked by browser privacy settings; dismissal should
    // still work for the current render without taking down the app.
  }
}

/** Browser-native PWA install banner with privacy-mode-safe dismissal storage. */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(wasDismissed);

  useEffect(() => {
    function handlePrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', handlePrompt);
    return () => window.removeEventListener('beforeinstallprompt', handlePrompt);
  }, []);

  if (!deferredPrompt || dismissed) return null;

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  }

  function handleDismiss() {
    setDismissed(true);
    rememberDismissed();
    setDeferredPrompt(null);
  }

  return (
    <div className="fixed left-0 right-0 top-0 z-[150] flex items-center justify-between bg-primary px-4 py-2 text-sm text-white shadow-md">
      <span>Install ClinikChat for a better experience</span>
      <div className="flex items-center gap-2">
        <button
          onClick={handleInstall}
          className="rounded bg-white px-3 py-1 text-xs font-medium text-primary hover:bg-gray-100"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="p-1 text-white/70 hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
